import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import './MovimentacoesPoligonos.css';

type VeiculoCard = {
  placa: string; motorista: string; poligono: string; entrada: string;
  saida: string | null; minutos: number; status: 'dentro' | 'saiu';
};
type Card = {
  id: string; dia: string; tipo: 'coleta' | 'entrega'; cliente: string; endereco: string;
  previsto: string; poligonos: Array<{ id: number; nome: string }>;
  programacoes: Array<{ cotacao: number; coleta: number; placa: string; carreta: string; motorista: string }>;
  placasProgramadas: Array<{ placa: string; carreta: string; motorista: string; localizacaoAtual?: string; poligonoAtual?: string | null; distanciaDestinoKm?: number | null; tempoAteDestino?: string | null; ultimaComunicacao?: string | null }>;
  veiculos: VeiculoCard[];
};
type Dia = { data: string; nome: string; cards: Card[] };
type Dados = {
  atualizadoEm: string; periodo: { inicio: string; fim: string }; dias: Dia[];
  resumo: { clientes: number; dentro: number; concluidos: number; semPoligono: number };
};

const hoje = () => {
  const agora = new Date();
  return `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}-${String(agora.getDate()).padStart(2, '0')}`;
};
const hora = (valor?: string | null) => valor ? valor.slice(11, 16) : '--:--';
const dataCurta = (valor: string) => valor.split('-').reverse().slice(0, 2).join('/');
const duracao = (minutos: number) => minutos >= 60 ? `${Math.floor(minutos / 60)}h ${minutos % 60}min` : `${minutos}min`;

const MovimentacoesPoligonos: React.FC = () => {
  const [referencia, setReferencia] = useState(hoje);
  const [dados, setDados] = useState<Dados | null>(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);

  const carregar = useCallback(async (silencioso = false) => {
    if (!silencioso) setCarregando(true);
    try {
      const resposta = await axios.get<Dados>('/api/monitoramento/poligonos/movimentacoes', { params: { data: referencia } });
      // Evita que uma resposta iniciada antes da meia-noite volte a exibir o dia anterior.
      if (referencia === hoje()) {
        setDados(resposta.data);
        setErro('');
      }
    } catch {
      if (referencia === hoje()) {
        setErro('N\u00e3o foi poss\u00edvel carregar a programa\u00e7\u00e3o de hoje.');
      }
    } finally {
      setCarregando(false);
    }
  }, [referencia]);

  useEffect(() => {
    const atualizarDiaAtual = () => {
      const novaReferencia = hoje();
      setReferencia(atual => atual === novaReferencia ? atual : novaReferencia);
    };
    const aoVoltarParaTela = () => {
      if (document.visibilityState === 'visible') atualizarDiaAtual();
    };
    atualizarDiaAtual();
    const intervaloDia = window.setInterval(atualizarDiaAtual, 15000);
    window.addEventListener('focus', atualizarDiaAtual);
    document.addEventListener('visibilitychange', aoVoltarParaTela);
    return () => {
      window.clearInterval(intervaloDia);
      window.removeEventListener('focus', atualizarDiaAtual);
      document.removeEventListener('visibilitychange', aoVoltarParaTela);
    };
  }, []);

  useEffect(() => {
    carregar();
    const intervalo = window.setInterval(() => carregar(true), 30000);
    return () => window.clearInterval(intervalo);
  }, [carregar]);

  const dias = dados?.dias || [];

  return (
    <main className="geo-page weekly">
      <header className="geo-header">
        <div><span>OPERA&Ccedil;&Atilde;O BROSE</span><h1>Programa&ccedil;&atilde;o de hoje por pol&iacute;gono</h1></div>
        <div className="geo-live"><i /> Atualiza&ccedil;&atilde;o autom&aacute;tica</div>
      </header>

      {erro && <div className="geo-error">{erro}</div>}
      <section className="geo-summary weekly-summary">
        <article className="scheduled"><small>CLIENTES / OPERA&Ccedil;&Otilde;ES</small><strong>{dados?.resumo.clientes ?? '-'}</strong><span>cards programados</span></article>
        <article className="inside"><small>VE&Iacute;CULOS DENTRO</small><strong>{dados?.resumo.dentro ?? '-'}</strong><span>nos pol&iacute;gonos agora</span></article>
        <article className="entry"><small>PASSAGENS CONCLU&Iacute;DAS</small><strong>{dados?.resumo.concluidos ?? '-'}</strong><span>entradas com sa&iacute;da</span></article>
      </section>

      {dados && <div className="geo-period">Hoje &bull; {dataCurta(dados.periodo.fim)}</div>}
      <section className="geo-week">
        {dias.map(dia => (
          <div className="geo-day" key={dia.data}>
            <header><div><span>{dia.nome}</span><b>{dataCurta(dia.data)}</b></div><em>{dia.cards.length} {dia.cards.length === 1 ? 'card' : 'cards'}</em></header>
            <div className="geo-day-cards">
              {!dia.cards.length && <p className="geo-day-empty">Nenhuma coleta ou entrega programada.</p>}
              {(['coleta', 'entrega'] as const).map(tipoCard => {
                const cardsTipo = dia.cards.filter(card => card.tipo === tipoCard);
                return <section className={`geo-card-lane ${tipoCard}`} key={tipoCard}>
                  <header>
                    <strong>{tipoCard === 'coleta' ? 'COLETAS' : 'ENTREGAS'}</strong>
                    <span>{cardsTipo.length}</span>
                  </header>
                  <div className="geo-card-lane-list">
                    {!cardsTipo.length && <p className="geo-lane-empty">Nenhuma {tipoCard} programada.</p>}
                    {cardsTipo.map(card => (
                <article className={`geo-client-card ${card.tipo} ${(card.veiculos || []).length ? 'has-arrival' : ''} ${(card.veiculos || []).some(v => v.status === 'dentro') ? 'vehicle-inside' : ''}`} key={card.id}>
                  <div className="geo-card-top">
                    <div><small>{card.tipo === 'coleta' ? 'COLETA' : 'ENTREGA'} &bull; {dataCurta(card.previsto.slice(0, 10))} &bull; {hora(card.previsto)}</small><h2>{card.cliente}</h2></div>
                    <span className={`geo-card-status ${(card.veiculos || []).some(v => v.status === 'dentro') ? 'active' : (card.veiculos || []).length ? 'done' : 'waiting'}`}>
                      {(card.veiculos || []).some(v => v.status === 'dentro') ? 'Ve\u00edculo dentro' : (card.veiculos || []).length ? 'Conclu\u00eddo' : 'Aguardando'}
                    </span>
                  </div>
                  <div className={`geo-polygon-name ${!(card.poligonos || []).length ? 'missing' : ''}`}>
                    <b>Pol&iacute;gono:</b> {(card.poligonos || []).length ? card.poligonos.map(p => p.nome).join(', ') : 'N\u00e3o vinculado ao CNPJ do cliente'}
                  </div>
                  <div className="geo-scheduled-vehicles">
                    <small>VE&Iacute;CULO{(card.placasProgramadas || []).length === 1 ? '' : 'S'} DA PROGRAMA&Ccedil;&Atilde;O</small>
                    {!(card.placasProgramadas || []).length && <span className="pending">Aguardando defini&ccedil;&atilde;o da placa</span>}
                    {(card.placasProgramadas || []).map(item => (
                      <span className="geo-programmed-vehicle" key={item.placa}>
                        <b>{item.placa}</b>{item.carreta && <> + {item.carreta}</>}
                        <em>{item.poligonoAtual || item.localizacaoAtual || 'Sem localização atual'}</em>
                        <small>{item.tempoAteDestino ? `${item.tempoAteDestino}${item.distanciaDestinoKm != null && item.tempoAteDestino !== 'No destino' ? ` • ${item.distanciaDestinoKm.toFixed(1)} km` : ''}` : 'Tempo até o destino indisponível'}</small>
                      </span>
                    ))}
                  </div>
                  <div className="geo-card-vehicles">
                    {!(card.veiculos || []).length && <div className="geo-waiting"><i /> Nenhum ve&iacute;culo programado entrou neste pol&iacute;gono</div>}
                    {(card.veiculos || []).map((veiculo, index) => (
                      <div className="geo-vehicle-row" key={`${veiculo.placa}-${veiculo.entrada}-${index}`}>
                        <div><small>VE&Iacute;CULO</small><strong>{veiculo.placa}</strong><span>{veiculo.motorista}</span></div>
                        <div><small>ENTRADA</small><strong>{hora(veiculo.entrada)}</strong>{veiculo.saida && <span>Sa&iacute;da {hora(veiculo.saida)}</span>}</div>
                        <em className={veiculo.status}>{veiculo.status === 'dentro' ? `Dentro h\u00e1 ${duracao(veiculo.minutos)}` : `Permaneceu ${duracao(veiculo.minutos)}`}</em>
                      </div>
                    ))}
                  </div>
                  <footer>{(card.programacoes || []).length} {(card.programacoes || []).length === 1 ? 'viagem programada' : 'viagens programadas'}</footer>
                </article>
                    ))}
                  </div>
                </section>;
              })}
            </div>
          </div>
        ))}
      </section>
      {carregando && !dados && <div className="geo-loading">Carregando programa&ccedil;&atilde;o...</div>}
      <footer className="geo-updated">&Uacute;ltima atualiza&ccedil;&atilde;o: {dados ? new Date(dados.atualizadoEm).toLocaleTimeString('pt-BR') : '--:--'}</footer>
    </main>
  );
};

export default MovimentacoesPoligonos;
